/* import __COLOCATED_TEMPLATE__ from './fin-identity.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { type PulseAccordion } from 'glint/pulse';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import { isPresent } from '@ember/utils';
interface Args {
  accordion: PulseAccordion;
  onContinue: () => void;
}

export default class FinIdentity extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @tracked newAgentId?: string;

  @action openSectionChanged() {
    this.newAgentId = undefined;
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.finStandaloneService.salesforceConfig!;
  }

  get salesforceSetupData() {
    return this.finStandaloneService.salesforceSetupData;
  }

  get isLoading() {
    return this.finStandaloneService.isLoadingSalesforceSetupData && !this.adminOptions.length;
  }

  get isDisabled() {
    return this.salesforceConfig.isPendingSetup;
  }

  get isComplete() {
    return !this.isDisabled && isPresent(this.selectedAgentName);
  }

  get adminOptions() {
    let admins = this.salesforceSetupData.admins?.map((admin) => ({
      text: admin.name,
      value: admin.id,
    }));
    return admins || [];
  }

  get selectedAgentName(): string | undefined {
    if (this.isLoading) {
      return undefined;
    }

    return this.adminOptions.find((option) => option.value === this.selectedAgentId)?.text;
  }

  get selectedAgentId(): string | undefined {
    if (this.salesforceConfig.finSalesforceUserId) {
      return this.salesforceConfig.finSalesforceUserId;
    }
    return undefined;
  }

  get isAgentUpdated() {
    return this.newAgentId && this.salesforceConfig.finSalesforceUserId !== this.newAgentId;
  }

  @action setAgentId(agentId: string) {
    this.newAgentId = agentId;
  }

  @action continue() {
    if (this.args.onContinue) {
      this.args.onContinue();
    }
  }

  @action async continueAndMaybeUpdateIdentity() {
    try {
      if (this.isAgentUpdated) {
        await this.salesforceConfig.setFinUser(this.newAgentId!);
        this.notificationsService.notifyConfirmation(
          this.intl.t(
            'standalone.deploy.salesforce.cases.integrate.fin-identity-section.success-notification',
          ),
        );
      }
      this.continue();
    } catch (e) {
      this.notificationsService.notifyResponseError(e, {
        default: this.intl.t(
          'standalone.deploy.salesforce.cases.integrate.fin-identity-section.error-notification',
        ),
      });
      console.error(e);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Cases::Integrate::FinIdentity': typeof FinIdentity;
    'standalone/deploy/salesforce/cases/integrate/fin-identity': typeof FinIdentity;
  }
}
